import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Sidebar } from './components/layout/Sidebar';
import { RightSidebar } from './components/layout/RightSidebar';
import { Analytics } from './components/pages/Analytics';
import Vergleich from './components/pages/Vergleich';
import { Load } from './components/pages/Load';
import Export from './components/pages/Export';
import { Simulation } from './components/pages/Simulation';
import { ProjectManagement } from './components/pages/ProjectManagement';
import { Leistungsverzeichnisse } from './components/pages/Leistungsverzeichnisse';
import { Dokumente } from './components/pages/Dokumente';
import { Erstpruefungen } from './components/pages/Erstpruefungen';
import { Header } from './components/layout/Header';
import { StatsCards } from './components/dashboard/StatsCards';
import { EarningsChart } from './components/dashboard/EarningsChart';
import { useDashboardData } from './hooks/useDashboardData';
import LandingPage from './pages/LandingPage';
import { useAuth } from './state/auth/context';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';

const HomePage = ({ dashboardData, refreshData, loading, error }) => {
  const { isExpanded, isRightExpanded } = useSidebar();
  const leftMargin = isExpanded ? 'ml-64' : 'ml-16';
  const rightMargin = isRightExpanded ? 'mr-64' : 'mr-12';

  // Show loading state for dashboard data
  if (loading) {
    return (
      <div className={`${leftMargin} ${rightMargin} h-screen flex items-center justify-center transition-all duration-300`}>
        <div>Loading dashboard data...</div>
      </div>
    );
  }

  // Show error state for dashboard data
  if (error) {
    return (
      <div className={`${leftMargin} ${rightMargin} h-screen flex items-center justify-center transition-all duration-300`}>
        <div>Error loading dashboard: {error}</div>
      </div>
    );
  }

  return (
    <>
      <div className={`${leftMargin} ${rightMargin} h-screen flex flex-col transition-all duration-300`}>
        <div className="pt-8 px-8 flex-shrink-0">
          <Header user={dashboardData?.user} refreshData={refreshData} />
          <StatsCards stats={dashboardData?.stats} />
        </div>
        <div className="flex-1 px-8 pb-8 min-h-0">
          <EarningsChart />
        </div>
      </div>
      <RightSidebar
        topCountries={dashboardData?.topCountries}
        topCustomers={dashboardData?.topCustomers}
        topProducts={dashboardData?.topProducts}
      />
    </>
  );
};

const ExportWrapper = () => {
  const { isExpanded } = useSidebar();
  const leftMargin = isExpanded ? 'ml-64' : 'ml-16';

  return (
    <div className={`${leftMargin} min-h-screen bg-gray-50 transition-all duration-300`}>
      <div className="p-8">
        <Export />
      </div>
    </div>
  );
};

const ProjectManagementWrapper = () => {
  const { isExpanded } = useSidebar();
  const leftMargin = isExpanded ? 'ml-64' : 'ml-16';

  return (
    <div className={`${leftMargin} min-h-screen bg-gray-50 transition-all duration-300`}>
      <ProjectManagement />
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Layout Component
const DashboardLayout = ({ children }) => (
  <div className="min-h-screen bg-gray-50">
    <Sidebar />
    {children}
  </div>
);

function App() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { dashboardData, loading: dataLoading, error, refreshData } = useDashboardData();

  // Show loading state only if auth is loading
  // Dashboard data loading should not block other routes
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <Routes>
      {/* Public route - Landing Page */}
      <Route
        path="/"
        element={
          isAuthenticated ?
            <Navigate to="/dashboard" replace /> :
            <LandingPage />
        }
      />

      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout>
            <HomePage
              dashboardData={dashboardData}
              refreshData={refreshData}
              loading={dataLoading}
              error={error}
            />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/analytics" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Analytics />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/load" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Load />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/export" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ExportWrapper />
          </DashboardLayout>
        </ProtectedRoute>
      } />
<Route path="/simulation" element={
  <ProtectedRoute>
    <DashboardLayout>
      <Simulation />
    </DashboardLayout>
  </ProtectedRoute>
} />

<Route path="/vergleich" element={
  <ProtectedRoute>
    <DashboardLayout>
      <Vergleich />
    </DashboardLayout>
  </ProtectedRoute>
} />

<Route path="/projects-management" element={
  <ProtectedRoute>
    <DashboardLayout>
      <ProjectManagementWrapper />
    </DashboardLayout>
  </ProtectedRoute>
} />

<Route path="/erstpruefungen" element={
  <ProtectedRoute>
    <DashboardLayout>
      <Erstpruefungen />
    </DashboardLayout>
  </ProtectedRoute>
} />

<Route path="/leistungsverzeichnisse" element={
  <ProtectedRoute>
    <DashboardLayout>
      <Leistungsverzeichnisse />
    </DashboardLayout>
  </ProtectedRoute>
} />

<Route path="/dokumente" element={
  <ProtectedRoute>
    <DashboardLayout>
      <Dokumente />
    </DashboardLayout>
  </ProtectedRoute>
} />
      </Routes>
    </SidebarProvider>
  );
}

export default App;